/**
 * The free-trial progress ring (ELARA-1042) — drawn around the account avatar in the sidebar, so how
 * much of a member's trial is left is visible on every page without any chrome of its own.
 *
 * Deliberately wordless. The ring says "something is running down"; the account menu it opens spells
 * out how long is left, and the Account page describes the membership. Nothing here is a gate — a
 * trial is an entitling status, and the member has full use of the app until it ends.
 *
 * Green, and green all the way down to the last day: a trial in progress is a good thing, and a ring
 * that turned amber then red would read as a fault the member has to fix rather than as a benefit
 * they are using.
 *
 * The one exception is `tone="destructive"`: a full red ring while a membership payment has failed
 * and Stripe is retrying it — that IS a fault the member has to fix, and it is not running down.
 */
import { cn } from '@elara/client-core/lib/utils';

/** Sized to the avatar button's 40pt box, which the ring is drawn inside. */
const RING_BOX = 40;
const RING_STROKE = 2;
/**
 * How far inside that box the stroke's outer edge sits.
 *
 * The button clips its content to a circle (`rounded-full` + `overflow-hidden`), and that circle is
 * the box's own edge — so a ring drawn hard against it comes back with its outer half shaved off,
 * uniformly, which reads as a thin broken hairline rather than as a ring.
 */
const RING_CLEARANCE = 1;
const RING_CENTRE = RING_BOX / 2;
const RING_RADIUS = (RING_BOX - RING_STROKE) / 2 - RING_CLEARANCE;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
/**
 * The smallest arc drawn, as a fraction of the ring.
 *
 * On the final day there is nothing left to sweep, and a bare track reads as a broken control rather
 * than as "nearly out" — so a sliver survives. It only ever under-states how much is left.
 */
const MIN_SWEEP = 0.04;

/** Track and sweep stroke classes per tone. */
const TONE_CLASSES = {
  success: { track: 'stroke-green-600/20 dark:stroke-green-500/20', sweep: 'stroke-green-600 dark:stroke-green-500' },
  destructive: { track: 'stroke-destructive/20', sweep: 'stroke-destructive' },
} as const;

export interface TrialRingProps {
  /** How much of the trial is still to run, 0–1. From `MembershipPaymentState.trial`. */
  fractionRemaining: number;
  /** Green for a running trial (the default); red, drawn full, for a failed membership payment. */
  tone?: keyof typeof TONE_CLASSES;
}

export function TrialRing({ fractionRemaining, tone = 'success' }: TrialRingProps) {
  const sweep = Math.max(fractionRemaining, MIN_SWEEP) * RING_CIRCUMFERENCE;
  const classes = TONE_CLASSES[tone];
  return (
    <svg
      viewBox={`0 0 ${RING_BOX} ${RING_BOX}`}
      fill="none"
      // Decorative: the account menu this sits on states the countdown in words.
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      <circle
        cx={RING_CENTRE}
        cy={RING_CENTRE}
        r={RING_RADIUS}
        strokeWidth={RING_STROKE}
        className={classes.track}
      />
      <circle
        cx={RING_CENTRE}
        cy={RING_CENTRE}
        r={RING_RADIUS}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={`${sweep} ${RING_CIRCUMFERENCE}`}
        // Swept clockwise from twelve o'clock, the direction a countdown is read.
        transform={`rotate(-90 ${RING_CENTRE} ${RING_CENTRE})`}
        className={cn(classes.sweep, 'transition-[stroke-dasharray] duration-500 ease-out')}
      />
    </svg>
  );
}
